import type { NavigateFunction } from "react-router-dom";
import apiFetch from "@/app/auth/apiFetch";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Structured interview synthesis produced by the LLM step. Same shape as the
 * recruiter-facing synthesis (see front/'s
 * `components/project/meetingTranscription.tsx` -> `SynthesisData`).
 */
export interface InterviewSynthesis {
  summary: string;
  key_points: string[];
  candidate_assessment: {
    strengths: string[];
    concerns: string[];
  };
  red_flags: string[];
  next_steps: string[];
  overall_rating: number;
}

export interface TranscribeTestResult {
  filename: string;
  size: number;
  language: string | null;
  transcript: string;
  synthesis: InterviewSynthesis | null;
}

/**
 * Canonical meeting-bot lifecycle values (see
 * back/src/features/interview/bot_providers/base.py). Same vocabulary the
 * recruiter UI maps in front/src/components/interview/botStatus.ts — admin/
 * keeps its own small local French-label mapping (see `BOT_STATUS_LABELS` in
 * `app/interview-tools/page.tsx`) instead of importing across the front/admin
 * package boundary.
 */
export type BotMappedStatus =
  | "scheduled"
  | "joining"
  | "waiting_room"
  | "in_call"
  | "post_processing"
  | "media_ready"
  | "completed"
  | "denied_entry"
  | "no_show"
  | "failed"
  | "too_short"
  | "cancelled";

export interface BotTestResult {
  bot_id: string;
  state: string | null;
}

export interface TranscriptEntity {
  text: string;
  label: "PERSON" | "ORG" | "SKILL";
  start: number;
  end: number;
}

export interface BotTranscriptUtterance {
  speaker_name: string;
  timestamp_ms: number | null;
  duration_ms: number | null;
  text: string;
  entities: TranscriptEntity[];
}

export interface BotTestStatus {
  bot_id: string;
  state: string | null;
  mapped_status: BotMappedStatus | null;
  recording_available: boolean;
  /** Live, real-name-attributed transcript — display-only, no Meet involved. */
  transcript: BotTranscriptUtterance[] | null;
}

export interface BotTestCancelResult {
  bot_id: string;
  cancelled: true;
}

/**
 * Transcribe an arbitrary audio file (file upload or a MediaRecorder
 * recording turned into a File) and optionally run the interview synthesis
 * on the resulting transcript. Can take 30-60s server-side (Whisper + LLM).
 */
export async function transcribeTest(
  navigate: NavigateFunction,
  file: File,
  synthesize: boolean,
): Promise<TranscribeTestResult> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("synthesize", synthesize ? "true" : "false");
  const res = await apiFetch(
    `${API_URL}/admin/interview-tools/transcribe`,
    { method: "POST", body: formData },
    navigate,
  );
  if (!res?.ok) {
    const detail = await res?.json().catch(() => null);
    throw new Error(
      typeof detail?.detail === "string" ? detail.detail : "Échec de la transcription",
    );
  }
  return res.json();
}

export interface SynthesizeTestResult {
  synthesis: InterviewSynthesis;
  /** Model alias that actually served the call (e.g. `recrutauto-synthesis`). */
  model: string;
  /**
   * Gateway base URL, or null when back/ called a provider directly. Null is a
   * finding, not a detail: it means the call bypassed the virtual key, the
   * budget and the single place where the RGPD register names the processor.
   */
  base_url: string | null;
  elapsed_ms: number;
}

/**
 * Run the interview synthesis on a pasted transcript — no audio, no Whisper.
 *
 * Exists to disambiguate: `transcribeTest(..., synthesize=true)` chains Whisper
 * THEN the LLM, so a failure accuses both. This one accuses exactly one.
 */
export async function synthesizeTest(
  navigate: NavigateFunction,
  transcript: string,
  candidateContext: string | null,
): Promise<SynthesizeTestResult> {
  const res = await apiFetch(
    `${API_URL}/admin/interview-tools/synthesize`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ transcript, candidate_context: candidateContext }),
    },
    navigate,
  );
  if (!res?.ok) {
    const detail = await res?.json().catch(() => null);
    throw new Error(
      typeof detail?.detail === "string" ? detail.detail : "Échec de la synthèse",
    );
  }
  return res.json();
}

/** Send a test meeting-bot to an arbitrary meeting URL (no candidate/campaign involved). */
export async function createTestBot(
  navigate: NavigateFunction,
  meetingUrl: string,
  botName: string | null,
): Promise<BotTestResult> {
  const res = await apiFetch(
    `${API_URL}/admin/interview-tools/bot`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ meeting_url: meetingUrl, bot_name: botName }),
    },
    navigate,
  );
  if (!res?.ok) {
    const detail = await res?.json().catch(() => null);
    throw new Error(
      typeof detail?.detail === "string" ? detail.detail : "Échec de l'envoi du bot",
    );
  }
  return res.json();
}

/** Poll the current state of a test bot run. */
export async function getTestBot(
  navigate: NavigateFunction,
  botId: string,
  testSkills?: string,
): Promise<BotTestStatus> {
  const query = testSkills ? `?test_skills=${encodeURIComponent(testSkills)}` : "";
  const res = await apiFetch(
    `${API_URL}/admin/interview-tools/bot/${encodeURIComponent(botId)}${query}`,
    { method: "GET" },
    navigate,
  );
  if (!res?.ok) {
    const detail = await res?.json().catch(() => null);
    throw new Error(
      typeof detail?.detail === "string"
        ? detail.detail
        : "Échec de la récupération du statut du bot",
    );
  }
  return res.json();
}

/** Cancel a test bot run (stops the bot, discards what it captured). */
export async function cancelTestBot(
  navigate: NavigateFunction,
  botId: string,
): Promise<BotTestCancelResult> {
  const res = await apiFetch(
    `${API_URL}/admin/interview-tools/bot/${encodeURIComponent(botId)}`,
    { method: "DELETE" },
    navigate,
  );
  if (!res?.ok) {
    const detail = await res?.json().catch(() => null);
    throw new Error(
      typeof detail?.detail === "string" ? detail.detail : "Échec de l'arrêt du bot",
    );
  }
  return res.json();
}
